import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 1-2: elevated start, pipes, big brick structures, more pits, koopas
export function buildLevel2(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1; // ground occupies y=-1,-2; walking surface is y=0
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [50, 52],
    [90, 93],
    [130, 132],
    [166, 168]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  // main ground
  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  // elevated starting platform: x 0..14, top at y=5
  for (let x = 0; x <= 14; x++) {
    for (let y = 0; y <= 4; y++) {
      setTile(x, y, TileType.Block);
    }
  }

  // ---- early section ----
  // brick row with mushroom ?
  setTile(18, 4, TileType.Brick);
  setTile(19, 4, TileType.Question, { kind: "mushroom" });
  setTile(20, 4, TileType.Brick);
  setTile(21, 4, TileType.Question, { kind: "coin" });
  setTile(22, 4, TileType.Brick);

  // first pipes
  const pipes: Array<[number, number]> = [
    [28, 2],
    [34, 3],
    [40, 4],
    [100, 2],
    [106, 3],
    [144, 3],
    [152, 4],
    [172, 2],
    [176, 3]
  ];
  const flowerPipes = new Set([40]); // pipes with flower on top
  for (const [px, h] of pipes) {
    const hasFlower = flowerPipes.has(px);
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe, undefined, hasFlower);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe, undefined, hasFlower);
    }
  }

  // high coins above the pipes
  setTile(31, 7, TileType.Question, { kind: "coin" });
  setTile(37, 8, TileType.Question, { kind: "coin" });

  // ---- after first pit: big brick structure ----
  setTile(56, 4, TileType.Brick);
  setTile(57, 4, TileType.Brick);
  setTile(58, 4, TileType.Question, { kind: "coin" });
  setTile(59, 4, TileType.Brick);
  setTile(60, 4, TileType.Question, { kind: "mushroom" });
  setTile(61, 4, TileType.Brick);
  setTile(62, 4, TileType.Brick);
  setTile(63, 4, TileType.Brick);
  // upper layer
  for (let x = 57; x <= 62; x++) setTile(x, 8, TileType.Brick);
  setTile(59, 8, TileType.Question, { kind: "coin" });
  setTile(60, 8, TileType.Question, { kind: "coin" });

  // floating coin row over the gap to the next section
  setTile(68, 4, TileType.Question, { kind: "coin" });
  setTile(69, 4, TileType.Question, { kind: "coin" });
  setTile(70, 4, TileType.Question, { kind: "coin" });

  // staircase up over second pit (56..60 up, 61..65 down)
  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };
  stair(76, 1);
  stair(77, 2);
  stair(78, 3);
  stair(79, 4);
  stair(80, 4);
  stair(81, 4);
  stair(82, 3);
  stair(83, 2);
  stair(84, 1);

  // ---- after second pit ----
  setTile(96, 4, TileType.Brick);
  setTile(97, 4, TileType.Question, { kind: "mushroom" });
  setTile(98, 4, TileType.Brick);
  setTile(99, 4, TileType.Question, { kind: "coin" });
  setTile(100, 4, TileType.Brick);
  setTile(101, 4, TileType.Brick);

  // arch of bricks
  for (let x = 112; x <= 122; x++) setTile(x, 4, TileType.Brick);
  setTile(115, 4, TileType.Question, { kind: "coin" });
  setTile(119, 4, TileType.Question, { kind: "mushroom" });
  for (let x = 114; x <= 120; x++) setTile(x, 8, TileType.Brick);
  setTile(116, 8, TileType.Question, { kind: "coin" });
  setTile(118, 8, TileType.Question, { kind: "coin" });

  // ---- after third pit ----
  setTile(136, 4, TileType.Brick);
  setTile(137, 4, TileType.Question, { kind: "coin" });
  setTile(138, 4, TileType.Brick);
  setTile(139, 4, TileType.Brick);
  setTile(140, 4, TileType.Brick);
  setTile(141, 4, TileType.Brick);
  setTile(142, 4, TileType.Brick);
  setTile(139, 7, TileType.Question, { kind: "coin" });
  setTile(140, 7, TileType.Question, { kind: "coin" });
  setTile(141, 7, TileType.Question, { kind: "coin" });

  // ---- before final pit: brick run ----
  for (let x = 156; x <= 164; x++) setTile(x, 4, TileType.Brick);
  setTile(158, 4, TileType.Question, { kind: "coin" });
  setTile(162, 4, TileType.Question, { kind: "coin" });

  // final staircase up: 181..184 heights 1..4, flat 185..186 at 4, down 187..190
  stair(181, 1);
  stair(182, 2);
  stair(183, 3);
  stair(184, 4);
  stair(185, 4);
  stair(186, 4);
  stair(187, 4);
  stair(188, 3);
  stair(189, 2);
  stair(190, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block); // pole top ball

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 20 },
    { kind: "goomba", x: 23 },
    { kind: "paratroopa", x: 31 },
    { kind: "goomba", x: 38 },
    { kind: "goomba", x: 44 },
    { kind: "goomba", x: 47 },
    { kind: "goomba", x: 55 },
    { kind: "goomba", x: 58 },
    { kind: "goomba", x: 66 },
    { kind: "goomba", x: 69 },
    { kind: "paratroopa", x: 73 },
    { kind: "goomba", x: 88 },
    { kind: "goomba", x: 97 },
    { kind: "goomba", x: 103 },
    { kind: "goomba", x: 109 },
    { kind: "paratroopa", x: 113 },
    { kind: "goomba", x: 124 },
    { kind: "goomba", x: 127 },
    { kind: "goomba", x: 134 },
    { kind: "koopa", x: 140 },
    { kind: "goomba", x: 148 },
    { kind: "goomba", x: 151 },
    { kind: "goomba", x: 157 },
    { kind: "goomba", x: 160 },
    { kind: "koopa", x: 170 },
    { kind: "goomba", x: 174 },
    { kind: "goomba", x: 178 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
